import { APP_ROUTES } from "../constants";
import type { IWidgetSession } from "../shared/session/interfaces/session";

/** Query param carrying the screen to resume once the agreement is accepted. */
export const TERMS_NEXT_PARAM = "next";

/**
 * True when the signed-in user has not accepted the current terms version.
 * The gate runs after bind, never on the public intention path.
 * Ignore `config.showAgreement` (deprecated).
 */
export const isTermsPending = (
  session?: IWidgetSession | null
): boolean => {
  if (!session) return false;
  return !session.termsAccepted;
};

/** Unavailable destinations must not be wrapped in Welcome. */
export const isUnavailablePath = (path: string): boolean =>
  path.split("?")[0] === APP_ROUTES.REQUEST_ALREADY_FULFILLED_PAGE;

export const shouldShowWelcomeBefore = (
  session: IWidgetSession | null | undefined,
  dest: string
): boolean => isTermsPending(session) && !isUnavailablePath(dest);

/** In-app continuation path only — never an absolute or protocol-relative URL. */
export const safeNextPath = (raw: string | null | undefined): string | null => {
  if (!raw || !raw.startsWith("/") || raw.startsWith("//")) return null;
  return raw;
};

/** Consent screen route that resumes `next` after acceptance. */
export const welcomePagePath = (next: string): string =>
  `${APP_ROUTES.WELCOME_PAGE}?${TERMS_NEXT_PARAM}=${encodeURIComponent(next)}`;

/**
 * Current route as a continuation target. `linkId` is dropped because
 * `useRouteWithToken` re-appends it on every navigation.
 */
export const currentPathForNext = (): string => {
  const { pathname, search } = window.location;
  const params = new URLSearchParams(search);
  params.delete("linkId");
  const query = params.toString();
  return query ? `${pathname}?${query}` : pathname;
};
